import { useContext, useEffect, useState } from "react";
import Button from "../../../../../components/bootstrap/Button";
import FormGroup from "../../../../../components/bootstrap/forms/FormGroup";
import Input from "../../../../../components/bootstrap/forms/Input";
import Popovers from "../../../../../components/bootstrap/Popovers";
import Spinner from "../../../../../components/bootstrap/Spinner";
import logo from '../../../../../assets/img/logo_uget_black.png';
import Money from "../../../../../helpers/utils/money/money";
import NineNineFoodWizard from "../../../../../components/modals/Delivery/NineNineFood/NineNineFoodWizard";
import TableCustomDelivery from "../../../../../components/MyCustom/TableCustomDelivery";
import Menu99FoodDB from "../../../../../database/wrappers/menu99Food";
import api from "../../../../../services/api";
import PreAlert from "../../../../../helpers/utils/preAlert";
import { documentId, where } from "firebase/firestore";
import { ResultsContext } from "../../../../../contexts/results";

const NineNineManager = ({ shouldOpenModal, sharedLocalData, loadingBaseData }) => {
    const { integrationsData } = useContext(ResultsContext)
    const { storeSelected, estabSelected } = sharedLocalData || {};

    const [menu99Data, setMenu99Data] = useState(null);
    const [categoriesData, setCategoriesData] = useState([]);
    const [showWizard, setShowWizard] = useState(false);
    const [loadingStatus, setLoadingStatus] = useState({});
    const [loadingPrices, setLoadingPrices] = useState({});

    useEffect(() => {
        if (!estabSelected || !storeSelected) return;

        const unsubscribe = new Menu99FoodDB(estabSelected, storeSelected).on(data => {
            const menu = data[0];
            if (!menu) {
                setMenu99Data(null);
                setCategoriesData([]);
                return;
            }

            setMenu99Data(menu);
            const itemsMap = menu.items || [];

            const groups = menu.categories?.map(cat => ({
                categoryId: cat.app_category_id,
                name: cat.category_name,
                products: itemsMap
                    .filter(item => cat.app_item_ids.includes(item.app_item_id))
                    .map(item => ({
                        id: item.app_item_id,
                        name: item.item_name,
                        image_url: item.head_img,
                        delivery_code: item.app_external_id,
                        nineNinePrice: item.price,
                        short_desc: item.short_desc,
                        priority: item.priority,
                        status: item.status ?? 1,
                        is_sold_separately: item.is_sold_separately ?? true,
                        app_modifier_group_ids: item.app_modifier_group_ids
                    }))
            })) || [];

            setCategoriesData(groups);
        }, where(documentId(), "==", "99"));

        return () => unsubscribe();
    }, [estabSelected, storeSelected]);

    useEffect(() => {
        if (shouldOpenModal) setShowWizard(true);
    }, [shouldOpenModal]);

    const handleStatusChange = async (product) => {
        const newStatus = product.status === 1 ? 2 : 1;
        setLoadingStatus(prev => ({ ...prev, [product.id]: true }));

        try {
            await api.post(`routes99/store/${integrationsData.store_99}/menu/item/status`, {
                app_item_ids: [product.id],
                status: newStatus
            });
            PreAlert.success("Status enviado! Aguarde o sync...");
        } catch (err) {
            PreAlert.error(err.response?.data?.message || "Erro ao atualizar o status");
        } finally {
            setLoadingStatus(prev => ({ ...prev, [product.id]: false }));
        }
    };

    const editPrice = (productId, masked) => {
        const cents = parseInt(masked.replace(/\D/g, "")) || 0;
        setCategoriesData(prev =>
            prev.map(cat => ({
                ...cat,
                products: cat.products.map(p =>
                    p.id === productId ? { ...p, nineNinePrice: cents } : p
                )
            }))
        );
    };

    const savePrice = async (product) => {
        setLoadingPrices(prev => ({ ...prev, [product.id]: true }));

        try {
            await api.post(`routes99/store/${integrationsData.store_99}/menu/item/update`, {
                app_item_id: product.id,
                item_name: String(product.name).slice(0, 50),
                price: product.nineNinePrice,
                status: product.status,
                is_sold_separately: product.is_sold_separately,
                app_external_id: product.delivery_code,
                app_modifier_group_ids: product.app_modifier_group_ids,
                short_desc: product.short_desc?.slice(0, 300),
                head_img: product.image_url,
                priority: product.priority
            });
            PreAlert.success("Preço enviado! Aguarde o sync...");
        } catch (error) {
            PreAlert.error("Erro ao atualizar preço");
        } finally {
            setLoadingPrices(prev => ({ ...prev, [product.id]: false }));
        }
    };

    const columns = [
        {
            label: "Imagem",
            field: "image_url",
            format: row => (
                <>
                    {row.image_url ? (
                        <img src={row.image_url} alt="Imagem" width={54} height={54} />
                    ) : (
                        <img src={logo} alt="Imagem" width={60} height='auto' />
                    )}
                </>
            )
        },
        { label: "Nome", field: "name" },
        { label: "Delivery Code", field: "delivery_code" },
        {
            label: "Preço",
            field: "nineNinePrice",
            format: row => (
                <FormGroup>
                    <Input
                        value={Money.centsToMaskMoney(row.nineNinePrice)}
                        onChange={e => editPrice(row.id, e.target.value)}
                        className="w-50"
                    />
                </FormGroup>
            )
        },
        {
            label: "Status",
            format: row =>
                loadingStatus[row.id] ? (
                    <Spinner />
                ) : (
                    <Button
                        isLink
                        color={row.status === 1 ? "success" : "warning"}
                        icon="Circle"
                        onClick={() => handleStatusChange(row)}
                    >
                        {row.status === 1 ? "Ativo" : "Desativado"}
                    </Button>
                )
        },
        {
            label: "Ações",
            format: row => (
                <Popovers trigger="hover" title="Salvar Preço">
                    <Button
                        isLink
                        onClick={() => savePrice(row)}
                        icon={loadingPrices[row.id] ? "HourglassEmpty" : "save"}
                        disabled={loadingPrices[row.id]}
                    />
                </Popovers>
            )
        }
    ];

    if (loadingBaseData) {
        return (
            <div className="d-flex justify-content-center p-5">
                <Spinner />
            </div>
        );
    }

    return (
        <>
            <TableCustomDelivery
                columns={columns}
                categoriesData={categoriesData}
                fileName="Produtos-99Food"
            />

            <NineNineFoodWizard
                open={showWizard}
                handleOpen={setShowWizard}
                existingMenuData={menu99Data}
            />
        </>
    );
};

export default NineNineManager;
